import React, { useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import LzMRights, { LzTRights } from '@lz-models/commons/Rights';
import { LzTOpenObject } from '@lz-types/types';
import LzXButtonsBar from '@lz-widgets/ButtonsBar';

export interface LzPRightsMatrix {
    schema: any;
    rights: LzTRights;
    onChange: (rights: LzTRights) => void;
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
}

const LzXRightsMatrix = (props: LzPRightsMatrix) => {
    const schemaGroups = LzMRights.getSchemaGroups(props.schema);
    const schemaActions = LzMRights.getSchemaActions(props.schema);

    const getExpandedData = (groups: Array<string>, value: boolean): any => {
        const out: LzTOpenObject = {};

        for (const group of groups) {
            out[group] = value;
        }

        return out;
    };

    const getCheckAllData = (groups: Array<string>, actions: Array<string>, value: boolean): any => {
        const out: LzTOpenObject = {};

        for (const group of groups) {
            out[group + '-all'] = value;
            for (const action of actions) {
                out[group + '-' + action] = value;
            }
            for (const item of LzMRights.getSchemaGroupItemsAsKeys(props.schema, group)) {
                out[group + '--' + item] = value;
            }
        }
        return out;
    };

    const [expanded, setExpanded] = useState(getExpandedData(schemaGroups, true));
    const [checkAll, setCheckAll] = useState(getCheckAllData(schemaGroups, schemaActions, false));

    const onChangeType = (val: number) => {
        props.onChange(LzMRights.setByType(val, props.schema));
    };

    const onCheckAll = (checked: boolean) => {
        setCheckAll(getCheckAllData(schemaGroups, schemaActions, checked));
        props.onChange(LzMRights.makeRightsBySchema(props.schema, checked));
    };

    const toggleCheckGroupAll = (group: any) => {
        const changed = Object.assign({}, props.rights);
        const items = LzMRights.getSchemaGroupItemsAsKeys(props.schema, group);
        const value = !checkAll[group + '-all'];
        const newChkAll = Object.assign({}, checkAll);
        newChkAll[group + '-all'] = value;

        for (const item of items) {
            newChkAll[group + '--' + item] = value;
            for (const action of schemaActions) {
                newChkAll[group + '-' + action] = value;
                changed[group][item][action] = value;
            }
        }

        props.onChange(changed);
        setCheckAll(newChkAll);
    };

    const toggleCheckGroupAction = (group: any, action: any) => {
        const changed = Object.assign({}, props.rights);
        const items = LzMRights.getSchemaGroupItemsAsKeys(props.schema, group);
        const value = !checkAll[group + '-' + action];

        for (const item of items) {
            changed[group][item][action] = value;
        }

        props.onChange(changed);
        setCheckAll(Object.assign({}, checkAll, { [group + '-' + action]: value }));
    };

    const toggleCheckGroupItem = (group: any, item: any) => {
        const changed = Object.assign({}, props.rights);
        const value = !checkAll[group + '--' + item];

        for (const action of schemaActions) {
            changed[group][item][action] = value;
        }

        props.onChange(changed);
        setCheckAll(Object.assign({}, checkAll, { [group + '--' + item]: value }));
    };

    const toggleCheckRight = (group: any, item: any, action: any) => {
        const changed = Object.assign({}, props.rights);
        changed[group][item][action] = !changed[group][item][action];
        props.onChange(changed);
    };

    const type = LzMRights.getType(props.rights);

    return (
        <React.Fragment>
            {false && <pre>{JSON.stringify(props.rights, null, 2)}</pre>}

            <Container style={{ height: 'auto', maxWidth: '100%', padding: 0 }}>
                <Grid container spacing={3} alignItems="center" style={{ width: '100%', margin: 0 }}>
                    <Grid size={{ xs: 6 }}>
                        <div style={{ paddingTop: 17 }}>
                            <RadioGroup row value={type} onChange={(evt) => onChangeType(+evt.target.value)}>
                                <FormControlLabel value={LzMRights.TYPE_ALLOW_ALL} control={<Radio />} label="Allow All" />
                                <FormControlLabel value={LzMRights.TYPE_DENY_ALL} control={<Radio />} label="Deny All" />
                                <FormControlLabel value={LzMRights.TYPE_CUSTOM} control={<Radio />} label="Custom Rights" />
                            </RadioGroup>
                        </div>
                    </Grid>
                    {type === LzMRights.TYPE_CUSTOM && (
                        <Grid size={{ xs: 6 }}>
                            <LzXButtonsBar paddingTop={0} paddingBottom={0}
                                buttons={[
                                    { title: 'Expand All', onClick: () => setExpanded(getExpandedData(schemaGroups, true)) },
                                    { title: 'Collapse All', onClick: () => setExpanded(getExpandedData(schemaGroups, false)) },
                                    { title: 'Check All', onClick: () => onCheckAll(true) },
                                    { title: 'Clear All', onClick: () => onCheckAll(false) },
                                ]}
                            />
                        </Grid>
                    )}
                </Grid>

                {type === LzMRights.TYPE_CUSTOM && (
                    <Table stickyHeader size="small" className="lz-rights-matrix-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <h3>Screens</h3>
                                </TableCell>
                                <TableCell>
                                    <h3>All</h3>
                                </TableCell>
                                {schemaActions.map((act: string, dex: number) => (
                                    <TableCell key={'act-' + dex} style={{ textTransform: 'capitalize' }}>
                                        {act}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schemaGroups.map((grpVal: string, grpDex: number) => {
                                const groupItems = LzMRights.getSchemaGroupItemsAsKeys(props.schema, grpVal);

                                return (
                                    <React.Fragment key={'grp-' + grpDex}>
                                        <TableRow className="group-row">
                                            <TableCell padding="none">
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        paddingTop: 12,
                                                        paddingBottom: 9,
                                                        fontWeight: 700,
                                                        textTransform: 'capitalize',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => setExpanded(Object.assign({}, expanded, { [grpVal]: !expanded[grpVal] }))}
                                                >
                                                    {expanded[grpVal] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                    <div style={{ paddingLeft: 4, lineHeight: 2 }}>{grpVal}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell padding="none" style={{ textAlign: 'center' }}>
                                                <Checkbox checked={checkAll[grpVal + '-all']} onClick={() => toggleCheckGroupAll(grpVal)} />
                                            </TableCell>
                                            {schemaActions.map((act: string, dex: number) => (
                                                <TableCell key={'grp-' + grpDex + 'act-' + dex} padding="none" style={{ textAlign: 'center' }}>
                                                    <Checkbox
                                                        checked={checkAll[grpVal + '-' + act]}
                                                        onClick={() => toggleCheckGroupAction(grpVal, act)}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {expanded[grpVal] && (
                                            <React.Fragment>
                                                {groupItems.map((giVal: string, giDex: number) => (
                                                    <TableRow key={'grp-' + grpDex + '-itm-' + giDex}>
                                                        <TableCell>
                                                            <h4 style={{ paddingLeft: 36, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{LzMRights.getLabelFromKey(giVal)}</h4>
                                                        </TableCell>
                                                        <TableCell padding="none" style={{ textAlign: 'center' }}>
                                                            <Checkbox
                                                                checked={checkAll[grpVal + '--' + giVal]}
                                                                onClick={() => toggleCheckGroupItem(grpVal, giVal)}
                                                            />
                                                        </TableCell>
                                                        {schemaActions.map((act: string, dex: number) => (
                                                            <TableCell key={'grp-' + grpDex + '-itm-' + giDex + 'act-' + dex} padding="none" style={{ textAlign: 'center' }}>
                                                                {props.rights[grpVal][giVal] && props.schema[grpVal][giVal].indexOf(act) >= 0 && (
                                                                    <Checkbox
                                                                        checked={props.rights[grpVal][giVal][act]}
                                                                        onClick={() => toggleCheckRight(grpVal, giVal, act)}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Container>
        </React.Fragment>
    );
};

export default LzXRightsMatrix;
